import { exceptionNickname } from "core/constants/names";
import { whitespaceRegex } from "core/constants/regex";

export const truncateFilename = (text: any, len: any, withBracket = true) => {
  if (!text) {
    return "";
  }

  const ext = text.substring(text.lastIndexOf(".") + 1, text.length).toLowerCase();
  let filename = text.replace("." + ext, "");

  if (filename.length <= len) {
    return text;
  }

  filename = filename.substr(0, len);

  if (text.length > len) {
    filename = filename + (withBracket ? "[...]" : "..");
  }

  return filename + "." + ext;
};

export const formatRupiah = (val: number) => {
  if (!val && val !== 0) return val;

  let replaceNumber = val.toString().replace(/[^.\d]/g, ""),
    split = replaceNumber.split("."),
    restNumber = split[0].length % 3,
    defineNumber = split[0].substr(0, restNumber),
    isThousand = split[0].substr(restNumber).match(/\d{1,3}/gi);

  if (isThousand) {
    let separator = restNumber ? "," : "";
    defineNumber += separator + isThousand.join(",");
  }
  return defineNumber;
};

export const formatRupiahDot = (val?: number | null) => {
  if (!val && val !== 0) return val;

  const rupiahVal = formatRupiah(val);

  if (typeof rupiahVal === "number") return val;

  return "Rp " + rupiahVal.replace(/,/g, ".");
};

/*
  Function formatArrayToOthersText
  Example
    Input : [R, Python, Java, Ruby, Javascript], "skills")
    Output : R, Python, Java, and 2 other skills
*/
export const formatArrayToOthersText = (arr: any, othersText: any) => {
  if (!arr) {
    return "-";
  }

  const totalItem = arr.length;
  if (totalItem === 0) {
    return "-";
  } else if (totalItem === 1) {
    return arr[0];
  } else if (totalItem === 2) {
    return `${arr[0]} and ${arr[1]}`;
  } else if (totalItem === 3) {
    return `${arr[0]}, ${arr[1]}, and ${arr[2]}`;
  }

  //Logic to print competencies list if competencies is 3 or more than 3
  const skills = arr.slice(0, 3).join(", ");
  const otherSkills = totalItem - 3;

  return `${skills}, and ${otherSkills} other ${othersText}`;
};

/*
  Function capitalizeString
  Example
    Input : "full_time"
    Output : "Full time"
*/
export const capitalizeString = (str: any, separator = "_") => {
  if (!str) return str;

  const strArr = str.split(separator);

  const capitalizeStrArr = strArr.map(
    (str: any) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
  );

  return capitalizeStrArr.join(" ");
};

/*
  Function formatStatusAvailability
  Example
    Input : "open_to_work"
    Output : "Open to Work"
*/
export const formatStatusAvailability = (status: any) => {
  const statusStringArr = status?.split("_");

  const capitalizeStringArr = statusStringArr?.map((string: any) => {
    if (["a", "to", "for"].includes(string)) {
      return string;
    }

    return string.charAt(0).toUpperCase() + string.slice(1);
  });

  return capitalizeStringArr?.join(" ");
};

/*
  Function formatSalaryRange
  Example
    Input : (5000, 10000)
    Output : Rp5.000,00 - Rp10.000,00
*/
export const formatSalaryRange = (lowest: any, highest: any) => {
  const isFalsyLowest = !lowest || isNaN(lowest);
  const isFalsyHighest = !highest || isNaN(highest);
  const lowestFormatted = !isFalsyLowest && `${formatRupiahDot(lowest.toString())},00`;
  const highestFormatted = !isFalsyHighest && `${formatRupiahDot(highest.toString())},00`;

  if (isFalsyLowest && isFalsyHighest) return "-";

  if (isFalsyLowest || lowest === 0) return "\u2264 " + highestFormatted;

  if (isFalsyHighest || highest === 0) return " \u2265 " + lowestFormatted;

  return `${lowestFormatted} - ${highestFormatted} `;
};

export const formatYearsOfExperience = (years: any) => {
  //Logic to print years of experience text
  let yearExperienceText = "-";
  if (parseInt(years) === 0) {
    yearExperienceText = "Fresh Graduates";
  } else if (parseInt(years) === 1) {
    yearExperienceText = "A year of experience";
  } else if (parseInt(years) > 1) {
    yearExperienceText = `${years} years of experience`;
  }

  return yearExperienceText;
};

export const formatToBytesNumber = (str = "1GB") => {
  if (!str) return 0;

  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const len = str.length;
  const sizeNumber = parseInt(str.slice(0, len - 2));
  const sizeType = str.slice(len - 2);
  const multiplier = Math.pow(
    1024,
    sizes.findIndex((v) => v === sizeType)
  );

  return sizeNumber * multiplier;
};

export const getNickname = (name: any, exceptionNames = exceptionNickname) => {
  if (!name) return "";

  const nameArr = name?.toLowerCase().replace(".", " ").split(whitespaceRegex);

  return capitalizeString(
    nameArr.find((name: any) => {
      return !exceptionNames.includes(name);
    })
  );
};

export const clampText = (str = "", length = 20) => {
  if (!str) return str;

  if (str?.length > length) {
    return str?.substring(0, length) + "...";
  }

  return str;
};

export const splitHttp = (str = "") => {
  return str?.split("https://www.")[1] || str?.split("http://www.")[1] || str;
};

export const capitalizeFirstChar = (str: string = "", separator = "") => {
  // example_text => Example Text
  if (!!separator) {
    return str
      .split(separator)
      .map((e) => {
        const firstChar = e?.charAt?.(0)?.toUpperCase?.();
        const remainingChars = e?.slice?.(1);

        return `${firstChar}${remainingChars}`;
      })
      .join(" ");
  }

  const firstChar = str?.charAt?.(0)?.toUpperCase?.();
  const remainingChars = str?.slice?.(1);

  return `${firstChar}${remainingChars}`;
};
